import React from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { SCHEDULE_TYPE } from "utils/constants/price";
import isEmpty from "lodash/isEmpty";
import { getFieldPath } from "utils/form";
import get from "lodash/get";
import ScheduleField from "components/ScheduleField";
import BillingPeriodField from "components/BillingPeriodField";
import AmountField from "components/AmountField";
import TextareaController from "components/TextareaController";
import { DEFAULT_PRICE_FIELDS, PRICE_FIELDS } from "utils/constants/fields";
import { useIsAuthed } from "utils/selectors";
import { useRouter } from "next/router";
import PATHS from "utils/paths";
import SegmentedControl from "components/SegmentedControl";
import { TAX_BEHAVIOR_OPTIONS } from "utils/constants/taxRate";
import { useStripeFeature } from "utils/hooks/stripe";
import { STRIPE_FEATURES } from "utils/constants/stripe";
import { UI_THEME } from "utils/constants/ui";

const CreatePriceForm = ({
  name = "",
  innerAmountRef,
  fields = DEFAULT_PRICE_FIELDS
}) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const isAuthed = useIsAuthed();

  const { pathname } = useRouter();
  const isLinkBuider = pathname === PATHS.FEATURES_LINKS_BUILDER;

  const formattedPricePath = getFieldPath(name, `formattedPrice`);
  const recurringPath = getFieldPath(name, `recurring`);
  const recurringIntervalPath = getFieldPath(name, `recurring.interval`);
  const recurringIntervalCountPath = getFieldPath(
    name,
    `recurring.interval_count`
  );
  const currencyPath = getFieldPath(name, `currency`);
  const nicknamePath = getFieldPath(name, `nickname`);
  const taxBehaviorPath = getFieldPath(name, `tax_behavior`);

  const formattedPrice = get(values, formattedPricePath);
  const recurringInterval = get(values, recurringIntervalPath);
  const currency = get(values, currencyPath);
  const taxBehavior = get(values, taxBehaviorPath);

  const taxEnabled = useStripeFeature({ feature: STRIPE_FEATURES.TAX });

  const taxBehaviorTouched = get(touched, taxBehaviorPath);

  const scheduleValue = isEmpty(recurringInterval)
    ? SCHEDULE_TYPE.ONE_TIME
    : SCHEDULE_TYPE.RECURRING;

  let header;

  let nodes = [];
  if (fields.indexOf(PRICE_FIELDS.FORMATTED_PRICE) > -1) {
    nodes.push(
      <AmountField
        innerRef={innerAmountRef}
        withCalculator
        value={formattedPrice}
        name={formattedPricePath}
        currency={currency}
        currencyPath={currencyPath}
        recurringIntervalPath={recurringIntervalPath}
        recurringInterval={recurringInterval}
      />
    );
  }

  const showNicknameField =
    fields.indexOf(PRICE_FIELDS.NICKNAME) > -1 && isAuthed && !isLinkBuider;
  if (showNicknameField) {
    nodes.push(
      <TextareaController
        name={"nickname"}
        label={"Nickname"}
        key={nicknamePath}
        rootPath={name}
        fieldKey={"nickname"}
        minRows={1}
      />
    );
  }

  if (fields.indexOf(PRICE_FIELDS.SCHEDULE) > -1) {
    nodes.push(
      <ScheduleField
        name={recurringPath}
        intervalName={recurringIntervalPath}
        value={scheduleValue}
      />
    );

    if (!isEmpty(recurringInterval)) {
      nodes.push(
        <BillingPeriodField
          intervalName={recurringIntervalPath}
          intervalCountName={recurringIntervalCountPath}
          value={recurringInterval}
        />
      );
    }
  }

  if (fields.indexOf(PRICE_FIELDS.TAX_BEHAVIOR) > -1 && taxEnabled) {
    const taxBehaviorProps = {
      options: TAX_BEHAVIOR_OPTIONS,
      theme: UI_THEME.SLIM,
      value: taxBehavior,
      onClick: (_, selection) => {
        if (!taxBehaviorTouched) {
          setFieldTouched(taxBehaviorPath, true);
        }
        setFieldValue(taxBehaviorPath, selection);
      }
    };

    nodes.push(
      <div className="w-100 pt3">
        <div className="f6 mb2 dib fw6 field__label">Tax behavior</div>
        <SegmentedControl {...taxBehaviorProps} />
      </div>
    );
  }

  return (
    <div className="flex flex-column w-100">
      {header}
      {nodes.map((node, nodeIx) => (
        <div key={nodeIx} className={nodeIx ? "pt3 mt2" : ""}>
          {node}
        </div>
      ))}
    </div>
  );
};

CreatePriceForm.propTypes = {
  name: PropTypes.string,
  name: PropTypes.string,
  product: PropTypes.object
};

export default CreatePriceForm;
